/**
 * NotificationPrompt - the one-time ask, just after signing up
 * -------------------------------------------------------------
 * Shown once, immediately after an account is created. It is the moment
 * somebody is most likely to say yes: they have just decided they want to be
 * here, and nothing has annoyed them yet.
 *
 * BOTH ROLES, not just students. It used to be students only, on the grounds
 * that they are the ones who need to hear about a new job - but a poster who
 * misses "somebody has applied to your chore" for two days is the other half
 * of the same problem, and notify() pushes to posters exactly as it does to
 * students. It also carries the home-screen instructions, which nobody was
 * ever given.
 *
 * WHY NOT JUST CALL requestPermission() ON SIGNUP
 * Because the browser's own permission box is blunt, unexplained, and can only
 * ever be shown once - a refusal is permanent and cannot be undone from the
 * site. Firing it at somebody who has no idea what it is for is how you lose
 * them for good. So this explains first, and the browser box only appears
 * after they have tapped a button that says what it is for.
 *
 * WHY IT NEVER NAGS
 * It marks itself done whichever button is pressed, including "not now". A
 * student who said no is not asked again - they can turn it on in Settings
 * whenever they want, and the modal says so.
 */

import { useEffect, useState } from 'react';
import { Bell } from 'lucide-react';

import { pushSupport, askAndSubscribe } from '../services/push';
import { authApi } from '../services/api';
import Modal from './Modal';
import InstallGuide from './InstallGuide';

import { useI18n } from '../hooks/useI18n';

// Set by the signup form, read here, cleared as soon as it has been shown.
const FLAG = 'choreconnect_ask_notifications';

/** Called by both signup forms the moment an account is created. */
export function markNotificationPromptDue() {
  try {
    window.localStorage.setItem(FLAG, '1');
  } catch {
    /* private browsing - the prompt is a nicety, not worth an error */
  }
}

export default function NotificationPrompt({ user }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [support, setSupport] = useState({ ok: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState('');

  useEffect(() => {
    if (!user) return;
    let due = false;
    try {
      due = window.localStorage.getItem(FLAG) === '1';
    } catch {
      due = false;
    }
    if (!due) return;

    const can = pushSupport();

    // Do not ask somebody who cannot say yes. If notifications are blocked or
    // this browser has no idea what they are, there is nothing to offer and a
    // pitch with no button under it is just noise at the worst possible moment
    // - the minute after signing up.
    //
    // The iPhone case is the exception and IS worth showing: "add it to your
    // home screen" is something they can actually go and do.
    if (!can.ok && can.reason !== 'ios-needs-install') {
      try {
        window.localStorage.removeItem(FLAG);
      } catch {
        /* nothing to do */
      }
      return;
    }

    setSupport(can);
    setOpen(true);
  }, [user]);

  /** Whatever they choose, do not ask a second time. */
  const close = () => {
    try {
      window.localStorage.removeItem(FLAG);
    } catch {
      /* nothing to do */
    }
    setOpen(false);
  };

  const turnOn = async () => {
    setBusy(true);
    const result = await askAndSubscribe();
    setBusy(false);

    if (result.ok) {
      // They said yes to the browser, so record that they want them - without
      // this the phone would be subscribed while the account still said no.
      try {
        await authApi.updateProfile({ alerts: { enabled: true } });
      } catch {
        /* the subscription is the important half */
      }
      setDone('on');
      setTimeout(close, 1600);
      return;
    }

    // Refused, or not possible. Say so briefly and get out of the way rather
    // than arguing with somebody who has just said no.
    setDone('off');
    setTimeout(close, 2200);
  };

  if (!open) return null;

  const isStudent = user.role === 'student';

  // An under-18 whose parent has not confirmed yet cannot be sent job alerts,
  // so say so rather than letting them wonder why nothing arrives.
  const waitingOnParent = isStudent && user.age < 18 && !user.guardianConsentGiven;

  return (
    <Modal
      open={open}
      onClose={close}
      title={
        done === 'on'
          ? t('notifyPrompt.allSet')
          : isStudent
            ? t('notifyPrompt.titleStudent')
            : t('notifyPrompt.titlePoster')
      }
      actions={
        done ? null : (
          <>
            <button type="button" className="btn btn-ghost btn-inline" onClick={close}>
              {t('notifyPrompt.notNow')}
            </button>
            {support.ok && (
              <button type="button" className="btn btn-primary btn-inline" onClick={turnOn} disabled={busy}>
                <Bell size={16} /> {busy ? t('notifyPrompt.justAMoment') : t('notifyPrompt.yesTellMe')}
              </button>
            )}
          </>
        )
      }
    >
      {done === 'on' ? (
        <p>
          {isStudent
            ? t('notifyPrompt.doneStudent')
            : t('notifyPrompt.donePoster')}
        </p>
      ) : done === 'off' ? (
        <p>
          {t('notifyPrompt.offBody')}
        </p>
      ) : (
        <div className="stack" style={{ gap: 'var(--space-sm)' }}>
          {/* WHY, AND THEN GET OUT OF THE WAY.
              This used to be three paragraphs and a fully expanded set of
              home-screen instructions, shown to somebody who had signed up
              thirty seconds earlier. On a laptop it was long; on a phone it
              was the entire screen, which is the one device the whole thing
              is about.
              What is left is the single reason, and the steps behind a tap.
              The rest of it - what we will and will not send, and the note
              for a student still waiting on a parent - is on the Settings
              page, which is where somebody goes when they have decided they
              care. */}
          <p>
            {isStudent
              ? t('notifyPrompt.pitchStudent')
              : t('notifyPrompt.pitchPoster')}
          </p>

          {waitingOnParent && (
            <p className="text-sm text-muted">
              {t('notifyPrompt.waitingOnParent')}
            </p>
          )}

          {/* The home-screen steps, right here rather than a promise that
              Settings explains it later. On an iPhone this is not a nicety:
              without it the button below cannot work at all, which is why the
              modal is shown to iPhone users even though there is nothing for
              them to press yet. Collapsed until somebody taps their device. */}
          <InstallGuide withNotifications={false} />
        </div>
      )}
    </Modal>
  );
}
